import type { DeviceSearchFilters } from '@/api/deviceApi'
import { orAll } from '@/components/filterable-search-bar/DeviceFiltersUtils'
import { buildGroupedDeviceSearchFilters } from '@/components/grouped-filter-bar'
import {
	type DeviceStatusDefinition,
	type DeviceStatusExpression,
	defaultStatusDefinition,
	matchableStatusDefinitions,
} from '@/types/deviceStatus'
import { asList } from '@/utils/asList'
import { matchesCondition } from '@/utils/derivedStateMatch'

/** Every device has a heartbeat, so it is a real column whatever the state keys list. */
const HEARTBEAT_COLUMN = 'heartbeat'

/**
 * Every leaf is NULL-free, so the `not`s below mean what they say: a device missing the column a
 * condition reads must land on the same side of the filter as the badge `matchDerivedState` gives it.
 */
const stateNode = (
	definition: DeviceStatusDefinition,
	stateColumns: ReadonlySet<string> | undefined,
): DeviceSearchFilters | null =>
	buildGroupedDeviceSearchFilters(
		asList(definition.condition),
		// A column deleted after the state was defined 400s the whole search, so its condition becomes the
		// answer the badge gives a device that lacks the column.
		stateColumns
			? condition =>
					condition.field === HEARTBEAT_COLUMN || stateColumns.has(condition.field)
						? undefined
						: matchesCondition(condition, {})
			: undefined,
		{ nullSafe: true },
	)

/**
 * Backend filter for "this derived column resolves to one of `stateNames`".
 *
 * A derived column has no column of its own, so filtering on it is expressed as the conditions that
 * define the state — those name real device_state columns the backend already understands.
 *
 * Match order is first-wins, so a device is in state k only if it also fails every earlier state;
 * without that `NOT`, a device the UI shows as `driving` could come back under `parked` too.
 * The default state is the complement of all the rest, so selecting every state covers every device.
 * Several selected states are OR'd together.
 */
export const derivedStateFilter = (
	expression: DeviceStatusExpression,
	stateNames: readonly string[],
	/** The project's device_state columns; unset trusts every column the expression names. */
	stateColumns?: ReadonlySet<string>,
): DeviceSearchFilters | null => {
	const selected = new Set(stateNames)
	const matchable = matchableStatusDefinitions(expression)
	// Built once: every selected state also negates the ones ahead of it, and the default negates them all.
	const matchableNodes = matchable.map(definition => stateNode(definition, stateColumns))
	const nodes: DeviceSearchFilters[] = []

	matchable.forEach((definition, index) => {
		const own = matchableNodes[index]
		if (own === null || !selected.has(definition.name)) {
			return
		}
		const earlier = matchableNodes.slice(0, index).filter((node): node is DeviceSearchFilters => node !== null)

		nodes.push(earlier.length > 0 ? { and: [own, { not: orAll(earlier) }] } : own)
	})

	const fallback = defaultStatusDefinition(expression)
	if (fallback && selected.has(fallback.name)) {
		const all = matchableNodes.filter((node): node is DeviceSearchFilters => node !== null)
		// With nothing to exclude the default claims every device, which is what leaving it unfiltered does.
		if (all.length > 0) {
			nodes.push({ not: orAll(all) })
		}
	}

	if (nodes.length === 0) {
		return null
	}
	return nodes.length === 1 ? nodes[0] : orAll(nodes)
}
